import { CalendarCheckIn02Icon, PauseCircleIcon, Time02Icon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { useContext, useMemo, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { resolveSurveyStatus, useFetchSurveyQuery } from "@/api/surveysApi";
import { useFetchOrganizationsByIdQuery } from "@/features/organizations/api/organizationsApi";
import { WebsiteContext } from "@/pages/website/WebsiteProvider";
import { useAppSelector, useOriginalImage } from "@/hooks";
import { IMAGE_PLACEHOLDER } from "@/constants/global";
import { getThumbnailImage } from "@/utils/media";
import SurveyResponseForm from "./components/SurveyResponseForm";

const SurveyAnswerPage = () => {
  const { organization: contextOrganization } = useContext(WebsiteContext);
  const { organizationId: paramOrgId, surveyId, organizationAlias } = useParams<{
    organizationId?: string;
    surveyId?: string;
    organizationAlias?: string;
  }>();
  const [searchParams] = useSearchParams();
  const activeOrgId = useAppSelector((state) => state.ui.activeOrganization);
  const [logoLoadError, setLogoLoadError] = useState(false);

  const resolvedOrgId = contextOrganization?.id || paramOrgId || activeOrgId;

  const orgQuery = useFetchOrganizationsByIdQuery(resolvedOrgId || skipToken, {
    skip: Boolean(contextOrganization) || !resolvedOrgId,
  });

  const organization = contextOrganization || orgQuery.data;

  const rawLogo = organization?.logo || organization?.isotype;
  const originalLogoUrl = useOriginalImage(rawLogo, resolvedOrgId);

  const thumbLogoUrl = useMemo(() => {
    if (!rawLogo || !resolvedOrgId) return "";
    const fileStr = Array.isArray(rawLogo) ? rawLogo[0] : rawLogo;
    if (fileStr.startsWith("http://") || fileStr.startsWith("https://")) {
      return fileStr;
    }
    return (
      getThumbnailImage({
        file: fileStr,
        organizationId: resolvedOrgId,
        size: "150x150",
      }) || ""
    );
  }, [rawLogo, resolvedOrgId]);

  const resolvedLogoUrl =
    originalLogoUrl && originalLogoUrl !== IMAGE_PLACEHOLDER
      ? originalLogoUrl
      : thumbLogoUrl && thumbLogoUrl !== IMAGE_PLACEHOLDER
        ? thumbLogoUrl
        : "";

  const surveyQuery = useFetchSurveyQuery({
    organizationId: resolvedOrgId || skipToken,
    surveyId: surveyId || skipToken,
  });

  if (surveyQuery.isLoading || orgQuery.isLoading) {
    return (
      <Stack flex={1} alignItems="center" justifyContent="center" py={8}>
        <CircularProgress />
      </Stack>
    );
  }

  const survey = surveyQuery.data;

  if (!survey || !resolvedOrgId) {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Paper sx={{ p: 4, borderRadius: 3, textAlign: "center" }}>
          <Stack spacing={2} alignItems="center">
            <Alert severity="info" sx={{ width: "100%" }}>
              Esta encuesta no fue encontrada o no está disponible.
            </Alert>
            {organizationAlias && (
              <Button variant="contained" component={Link} to={`/${organizationAlias}/menus`}>
                Ir al Menú
              </Button>
            )}
          </Stack>
        </Paper>
      </Container>
    );
  }

  const status = resolveSurveyStatus(survey);

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#f8fafc", py: 4 }}>
      <Container maxWidth="sm">
        <Paper sx={{ p: { xs: 2.5, sm: 4 }, borderRadius: 3 }}>
          <Stack spacing={3}>
            <Stack spacing={1.5} alignItems="center" textAlign="center">
              {resolvedLogoUrl && !logoLoadError ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 0.5,
                  }}
                >
                  <img
                    src={resolvedLogoUrl}
                    alt={organization?.title || "Logotipo"}
                    onError={(e) => {
                      if (
                        thumbLogoUrl &&
                        thumbLogoUrl !== IMAGE_PLACEHOLDER &&
                        e.currentTarget.src !== thumbLogoUrl
                      ) {
                        e.currentTarget.src = thumbLogoUrl;
                      } else {
                        setLogoLoadError(true);
                      }
                    }}
                    style={{
                      maxHeight: 68,
                      maxWidth: 180,
                      width: "auto",
                      height: "auto",
                      objectFit: "contain",
                    }}
                  />
                </Box>
              ) : organization?.title ? (
                <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
                  {organization.title}
                </Typography>
              ) : null}
              <Typography variant="h3" fontWeight={700}>
                {survey.title}
              </Typography>
            </Stack>

            {status === "paused" ? (
              <Stack spacing={2.5} alignItems="center" textAlign="center" py={3}>
                <HugeiconsIcon icon={PauseCircleIcon} size={48} color="#ed6c02" />
                <Chip size="small" color="warning" label="Pausada" />
                <Stack spacing={1}>
                  <Typography variant="h4" fontWeight={600}>
                    Encuesta en pausa temporalmente
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Por el momento esta encuesta no puede ser llenada porque se encuentra en pausa. Por favor, intenta nuevamente más tarde.
                  </Typography>
                </Stack>
                {organizationAlias && (
                  <Button variant="contained" component={Link} to={`/${organizationAlias}/menus`}>
                    Ir al Menú
                  </Button>
                )}
              </Stack>
            ) : status === "finished" ? (
              <Stack spacing={2.5} alignItems="center" textAlign="center" py={3}>
                <HugeiconsIcon icon={CalendarCheckIn02Icon} size={48} color="#d32f2f" />
                <Chip size="small" color="error" label="Finalizada" />
                <Stack spacing={1}>
                  <Typography variant="h4" fontWeight={600}>
                    Esta encuesta ha finalizado
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Esta encuesta ha concluido y ya no puede ser llenada. ¡Muchas gracias por tu interés y participación!
                  </Typography>
                </Stack>
                {organizationAlias && (
                  <Button variant="contained" component={Link} to={`/${organizationAlias}/menus`}>
                    Ir al Menú
                  </Button>
                )}
              </Stack>
            ) : status !== "active" || !survey.isPublished ? (
              <Stack spacing={2.5} alignItems="center" textAlign="center" py={3}>
                <HugeiconsIcon icon={Time02Icon} size={48} color="#64748b" />
                <Chip size="small" color="default" label="Borrador" />
                <Stack spacing={1}>
                  <Typography variant="h4" fontWeight={600}>
                    Encuesta aún no disponible
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Por el momento esta encuesta se encuentra en preparación y no puede ser llenada.
                  </Typography>
                </Stack>
                {organizationAlias && (
                  <Button variant="contained" component={Link} to={`/${organizationAlias}/menus`}>
                    Ir al Menú
                  </Button>
                )}
              </Stack>
            ) : (
              <SurveyResponseForm
                survey={survey}
                organizationId={resolvedOrgId}
                orderId={searchParams.get("orderId")}
                branchId={searchParams.get("branchId")}
                tableId={searchParams.get("tableId") || searchParams.get("table")}
                flow={searchParams.get("flow")}
              />
            )}
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
};

export default SurveyAnswerPage;
